import React, {
  useState,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import _ from 'lodash';
import {
  Input,
  Button,
  Badge,
  Collapse,
} from 'reactstrap';
import { useIntl, injectIntl } from 'react-intl';

import {
  fetchCollections,
  clearCollectionsList,
  clearCollectionFilters,
  setCollectionsParams,
  refreshCollections,
} from '../../actions/Collections';
import {
  fetchFiles,
  clearFilesList,
  addSelectedFilter,
  removeSelectedFilter,
  clearFilesFiltersAndReload,
} from '../../actions/Files';
import {
  setSearchFields,
  setSearchTerm,
} from '../../actions/Search';

import FiltersResetButton from './FiltersResetButton';
import FiltersSearchFields from './FiltersSearchFields';
import HelpIcon from '../HelpIcon';
import SearchQueryType from '../Search/SearchQueryType';

import { isCollectionPage } from '../../helpers/Collections';
import { handleComponentParams, handleURLParams } from '../../helpers/URLParams';

import useResponsiveItemsPerPage from '../../hooks/useResponsiveItemsPerPage';

const FiltersSearch = ({
  search,
  customSearch,
  customSetSearch,
  customSetSearchFields,
  customRemoveSearchFields,
  customSelectedSearchFields,
  customSetQueryType,
  customSearchQueryType,
  type,
  setSearch,
  collection,
  allowDisabled,
  resetSearch,
  customResetSearch,
  showQueryType,
  prefix,
}) => {
  const [searchTermValue, setSearchTermValue] = useState();
  const [isOpen, setIsOpen] = useState(true);
  const [initialized, setInitialized] = useState(false);

  const intl = useIntl();
  const itemsPerPage = useResponsiveItemsPerPage();
  const history = useHistory();
  const location = useLocation();

  const shouldDisableField = allowDisabled && collection && collection.smart && collection.attributes?.filters?.find(f => f.type === 'search');

  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };

  const prevLocation = usePrevious(location);
  const prevSearch = usePrevious(search);

  useEffect(() => {
    if (customSearch !== undefined) {
      setSearchTermValue('');
    }
  }, [customSearch]);

  useEffect(() => {
    if (customSearch) {
      setSearchTermValue(customSearch);
    } else if (
      location.pathname === '/results' ||
      location.pathname === '/files' ||
      location.pathname === '/collections' ||
      isCollectionPage() ||
      location.pathname === '/archive/files' ||
      location.pathname === '/archive/collections'
    ) {
      const shouldForce = (
        (location.pathname === '/archive/files' && prevLocation?.pathname !== '/archive/files') ||
        (location.pathname === '/archive/collections' && prevLocation?.pathname !== '/archive/collections')
      );

      handleComponentParams(
        shouldForce || !initialized || prevSearch !== search,
        ['search'],
        { location },
        [(searchFromUrl) => {
          if (searchFromUrl !== null && (searchFromUrl !== searchTermValue || shouldForce)) {
            if (search !== searchFromUrl || shouldForce) {
              setSearch(searchFromUrl, itemsPerPage);
            }
            setSearchTermValue(searchFromUrl);
          } else if (searchFromUrl === null && searchTermValue) {
            setSearchTermValue('');
            resetSearch();
          }
          if (!initialized) {
            setInitialized(true);
          }
        }],
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, customSearch]);

  const handleReset = () => {
    setSearchTermValue('');
    if (customResetSearch) {
      customResetSearch();
    } else {
      resetSearch();
      handleURLParams({ search: null, search_fields: [] }, history);
    }
  };

  const handleKeyUp = (e) => {
    if (e.currentTarget.id !== `${prefix}checkFileSearch`) return;

    if (e.key === 'Enter') {
      setSearch(searchTermValue, itemsPerPage);
      if (!customSetSearch) {
        handleURLParams({ search: searchTermValue }, history);
      }
    }
  };

  const handleChange = (e) => {
    const { value } = e.target;
    setSearchTermValue(value);
  };

  return (
    <div className="files-filter file-search-filter">
      <div className="filter-header">
        <Button onClick={() => setIsOpen(!isOpen)} className="filter-title">
          <i className={`fal fa-angle-${isOpen ? 'up' : 'down'}`} />
          <span>{intl.formatMessage({ defaultMessage: 'Search' })}</span>
          {' '}
          {searchTermValue && (
            <Badge color="primary">
              1
            </Badge>
          )}
        </Button>
        {searchTermValue && (
          <FiltersResetButton
            handleReset={handleReset}
          />
        )}
      </div>
      <Collapse
        isOpen={isOpen}
        className="filter-body"
      >
        <div className="filter-form file-search-filter-form">
          {showQueryType && (
            <>
              <SearchQueryType
                customSetQueryType={customSetQueryType}
                customSearchQueryType={customSearchQueryType}
                prefix="modal-"
              />
              <div className="mb-2" />
            </>
          )}
          {(prefix !== 'modal-' && collection) && (
            <div className="file-search-filter-collection">
              Search in <strong>{collection.name}</strong>
            </div>
          )}
          <div className="file-search-filter-form-input-wrapper">
            <Input
              type="text"
              className={classnames({ disabled: shouldDisableField })}
              name={`${prefix}search`}
              id={`${prefix}checkFileSearch`}
              placeholder={intl.formatMessage({ defaultMessage: 'Search' })}
              value={searchTermValue || ''}
              onKeyUp={handleKeyUp}
              onChange={handleChange}
              disabled={shouldDisableField}
            />
            {shouldDisableField && (
              <HelpIcon id="smart-collection-disable-search-filter" permanent={true}>
                {intl.formatMessage({
                  defaultMessage: 'Search filter is disabled when Smart Filter for search is enabled',
                })}
              </HelpIcon>
            )}
          </div>
          {customSetSearch && (searchTermValue !== customSearch) && (
            <small className="mt-2">
              {intl.formatMessage({
                defaultMessage: 'Press Enter to apply search term',
              })}
            </small>
          )}
          {!shouldDisableField && (
            <FiltersSearchFields
              type={type}
              search={searchTermValue}
              prefix={prefix}
              external={!!customSetSearch}
              customSetSearchFields={customSetSearchFields}
              customRemoveSearchFields={customRemoveSearchFields}
              customSelectedSearchFields={customSelectedSearchFields}
            />
          )}
        </div>
      </Collapse>
    </div>
  );
};

const mapStateToProps = (state, props) => ({
  search: _.isString(props.customSearch) ? props.customSearch : state.Search.search,
  searchFields: state.Search.search_fields,
  collection: state.Collections.collection,
});

const mapDispatchToProps = (dispatch, props) => ({
  setSearch: props.customSetSearch || ((search, perPage) => {
    const { type, intl } = props;
    dispatch(removeSelectedFilter('search'));
    dispatch(addSelectedFilter({
      type: 'search',
      key: `${search}`,
      label: intl.formatMessage({ defaultMessage: 'Search: {search}' }, { search }),
    }));
    dispatch(setSearchTerm(search));

    switch (type) {
      case 'collections':
        dispatch(clearCollectionsList());
        dispatch(fetchCollections());
        break;
      case 'files':
        dispatch(clearFilesList());
        dispatch(fetchFiles());
        break;
      default:
        dispatch(clearFilesList());
        dispatch(fetchFiles());
        dispatch(clearCollectionsList());
        dispatch(setCollectionsParams({ per_page: perPage }));
        dispatch(fetchCollections());
        break;
    }
  }),
  resetSearch: props.customResetSearch || (() => {
    dispatch(setSearchTerm(''));
    dispatch(setSearchFields([]));
    dispatch(clearCollectionsList());
    dispatch(clearCollectionFilters());
    dispatch(refreshCollections());
    dispatch(clearFilesFiltersAndReload(true));
  }),
});

FiltersSearch.propTypes = {
  search: PropTypes.string,
  customSearch: PropTypes.string,
  type: PropTypes.string,
  setSearch: PropTypes.func.isRequired,
  customSetSearch: PropTypes.func,
  customResetSearch: PropTypes.func,
  customSetSearchFields: PropTypes.func,
  customRemoveSearchFields: PropTypes.func,
  customSelectedSearchFields: PropTypes.array,
  customSetQueryType: PropTypes.func,
  customSearchQueryType: PropTypes.object,
  resetSearch: PropTypes.func.isRequired,
  collection: PropTypes.object,
  allowDisabled: PropTypes.bool,
  showQueryType: PropTypes.bool,
  prefix: PropTypes.string,
};

FiltersSearch.defaultProps = {
  search: null,
  customSearch: null,
  type: null,
  customSetSearch: undefined,
  customSetSearchFields: undefined,
  customRemoveSearchFields: undefined,
  customSelectedSearchFields: undefined,
  customSetQueryType: undefined,
  customSearchQueryType: undefined,
  collection: null,
  allowDisabled: false,
  showQueryType: false,
  prefix: '',
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(FiltersSearch));
